/**
 * X reports `approval_status` as a string, and its account reference documents
 * one value: `ACCEPTED`. That is the only value given a meaning here. Anything
 * else is shown as X spelled it, with a caution — translating a string nobody
 * has documented would be a guess presented as a state.
 *
 * Source (v12, GET accounts / accounts/:account_id):
 * https://developer.x.com/en/docs/x-ads-api/campaign-management/api-reference/accounts
 */
export function approvalStatus(value: string | null): {
	label: string;
	tone: "ok" | "warn" | "err";
} {
	const v = value?.trim();
	if (!v) return { label: "Unknown", tone: "warn" };
	if (v === "ACCEPTED") return { label: "Approved", tone: "ok" };
	return { label: v, tone: "warn" };
}

/*
 * The spend helpers that used to live here — `spendCapFromMinor`,
 * `summariseCaps`, `spendPosition` — are gone, not moved.
 *
 * They read a Meta account's `spend_cap` and `amount_spent`, in minor units,
 * with 0 meaning "no limit". An X ad account carries neither: the nearest
 * figures sit on its funding instruments (`credit_limit_local_micro`, in
 * micros), and which one is the hard stop has not been decided. Until it is, a
 * cap is always null, and every screen built on it said "No limit set" and
 * "Set one in X Ads Manager" about every account — an instruction nobody can
 * carry out. Both fields come back together, with their source, a migration and
 * a contract entry of their own.
 */

/**
 * Where the status beside it came from, and when.
 *
 * 🔴 The approval status an account card shows is the mirror's — the row that
 * `POST /x-accounts/sync` last wrote — and not X's answer of this minute. On
 * 2026-10-20 the card said REJECTED off a sync of 2026-10-08 07:18Z while X had
 * already cleared the account (audits/2026-10-20/SUMMARY.md): a badge with no date is
 * read as the present. So the sentence names the mirror, carries the sync's time as
 * the Ad accounts table does, and says where a fresh read is. `format` is the date's
 * rendering, injectable so a case can pin the sentence without pinning a locale.
 */
export function syncedLabel(
	syncedAt: string | null,
	format: (iso: string) => string = (iso) => new Date(iso).toLocaleString(),
): string {
	if (syncedAt === null)
		return "Status from the mirror, never synced: X has not been asked about this account yet.";
	return `Status from the mirror, synced ${format(syncedAt)}. X may have changed since — Sync from X on Ad accounts reads it again.`;
}

/** Prefer a human handle, but never render an empty string as the identity. */
export function displayName(me: { email?: string; id?: string } | null): string {
	const email = me?.email?.trim();
	if (email) return email;
	const id = me?.id?.trim();
	if (id) return id;
	return "your G1 account";
}

/**
 * An ad account's currency, said where it lives. The worker answers `currency: null` for
 * every account in v1 (the contract): X keeps no currency on the account object, only on
 * its funding instruments, and which of several is «the» one is undecided. A dash there
 * read as «this account has no currency» while its instrument said EUR
 * (audits/2026-10-20/spa-review.md item 9). Plural, as the contract has it: an account can
 * hold several. Not `accountCurrency` — stats.ts has that name for a different question
 * (the one currency of an account's instruments, or null), and typecheck cannot tell
 * the two apart (-98's 🔵 on W8).
 */
export const CURRENCY_ON_INSTRUMENT = "On its funding instruments";

export function accountCurrencyText(currency: string | null): string {
	return currency ?? CURRENCY_ON_INSTRUMENT;
}
